import React, { useState, useEffect } from 'react';
import { Shield, ShieldOff, Copy, RefreshCw, Check, Loader2, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetDiagnosticConfig = () => {
  const [config, setConfig] = useState({ enabled: false, token: null });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/diagnostic/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setConfig(data);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/diagnostic/enable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setConfig({ enabled: true, token: data.token });
    } catch (error) {
      console.error('Erreur activation:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Désactiver l\'accès diagnostic à distance ?')) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/diagnostic/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConfig({ enabled: false, token: null });
    } catch (error) {
      console.error('Erreur désactivation:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Générer un nouveau lien ? L\'ancien sera invalidé.')) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/diagnostic/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setConfig({ enabled: true, token: data.token });
    } catch (error) {
      console.error('Erreur régénération:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const getDiagnosticUrl = () => {
    if (!config.token) return '';
    // Utiliser l'URL de production si disponible, sinon l'URL actuelle
    const baseUrl = window.location.origin.includes('localhost') 
      ? API_URL 
      : window.location.origin;
    return `${baseUrl}/api/diagnostic/${config.token}`;
  };

  const handleCopy = async () => {
    const url = getDiagnosticUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`px-6 py-4 ${config.enabled ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-gray-500 to-gray-600'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              {config.enabled ? (
                <Shield className="w-5 h-5 text-white" />
              ) : (
                <ShieldOff className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Diagnostic à distance</h3>
              <p className="text-white/80 text-sm">
                {config.enabled ? 'Accès activé' : 'Accès désactivé'}
              </p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${config.enabled ? 'bg-white/20 text-white' : 'bg-white/10 text-white/70'}`}>
            {config.enabled ? '● Actif' : '○ Inactif'}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-sm text-gray-600 mb-4">
          Permet à un développeur d'accéder aux informations de diagnostic de votre application 
          sans avoir besoin de se connecter. Utile pour le support à distance.
        </p>

        {config.enabled && config.token && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lien de diagnostic (à partager) :
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={getDiagnosticUrl()}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono text-gray-700"
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                  copied 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copié
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copier
                  </>
                )}
              </button>
              <a
                href={getDiagnosticUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition"
                title="Ouvrir dans un nouvel onglet"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}

        {/* Warning */}
        {config.enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              ⚠️ <strong>Attention :</strong> Ce lien donne accès à des informations sensibles. 
              Ne le partagez qu'avec des personnes de confiance et désactivez-le après utilisation.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          {config.enabled ? (
            <>
              <button
                onClick={handleRegenerate}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Nouveau lien
              </button>
              <button
                onClick={handleDisable}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg font-medium hover:bg-red-100 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ShieldOff className="w-4 h-4" />
                Désactiver
              </button>
            </>
          ) : (
            <button
              onClick={handleEnable}
              disabled={actionLoading}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              Activer l'accès diagnostic
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetDiagnosticConfig;
